import "./login.css";

import { DEFAULT_COUNTRY, detectCountryCode, getCountryList } from "../../service/service";
import { Link, Navigate, useNavigate, useSearchParams } from "react-router-dom";
import React, { useEffect, useMemo, useState } from "react";

import { API_URL } from "../../../env";
import VyntezBuddyLogo from "../../vyntezbuddy/VyntezBuddyLogo";
import axios from "axios";
import { useAppToast } from "../../vyntezbuddy/AppToastProvider";
import { useVBTranslation } from "../../vyntezbuddy/language";

const Login = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const toast = useAppToast();
  const { t, language, setLanguage, options } = useVBTranslation();

  const [countries, setCountries] = useState([DEFAULT_COUNTRY]);
  const [selectedCountryCode, setSelectedCountryCode] = useState(DEFAULT_COUNTRY.code);
  const [phoneNumber, setPhoneNumber] = useState("");
  const [otp, setOtp] = useState("");
  const [serverOtp, setServerOtp] = useState("");
  const [username, setUsername] = useState("");
  const [otpSent, setOtpSent] = useState(false);
  const [secondsLeft, setSecondsLeft] = useState(0);
  const [acceptTerms, setAcceptTerms] = useState(false);
  const [acceptPrivacy, setAcceptPrivacy] = useState(false);
  const [loading, setLoading] = useState(false);

  const isLoggedIn = Boolean(sessionStorage.getItem("token"));
  const selectedCountry = useMemo(
    () => countries.find((item) => item.code === selectedCountryCode) || DEFAULT_COUNTRY,
    [countries, selectedCountryCode]
  );
  const isOldUser = (username || "").toLowerCase() === "olduser";
  const requiresConsent = Boolean(username) && !isOldUser;
  const userLabel = username ? (isOldUser ? "Existing user" : "New user") : "Waiting for sign-in";

  useEffect(() => {
    if (!secondsLeft) {
      return;
    }

    const timer = window.setInterval(() => {
      setSecondsLeft((value) => (value > 0 ? value - 1 : 0));
    }, 1000);

    return () => window.clearInterval(timer);
  }, [secondsLeft]);

  useEffect(() => {
    const fetchCountries = async () => {
      try {
        const rows = await getCountryList();
        if (rows.length > 0) {
          setCountries(rows);
        }
      } catch (error) {
        console.error("Country list fallback is active", error);
      }
    };

    fetchCountries();
  }, []);

  useEffect(() => {
    const applyDetectedCountry = async () => {
      const code = await detectCountryCode();
      setSelectedCountryCode(code);
    };

    applyDetectedCountry();
  }, []);

  const formatTimer = (value) => {
    const minute = Math.floor(value / 60)
      .toString()
      .padStart(2, "0");
    const second = (value % 60).toString().padStart(2, "0");
    return `${minute}:${second}`;
  };

  const buildMobileNumber = () => `${selectedCountry.dialCode}${phoneNumber}`.replace(/\s+/g, "");

  const sendOtp = async (isResend = false) => {
    const digits = phoneNumber.replace(/\D/g, "");
    if (digits.length < 8) {
      toast.showInfo("Please enter a valid phone number.");
      return;
    }

    setLoading(true);

    try {
      const mobile = digits;
      const primaryPhoneCountryCode = selectedCountry.dialCode;
      const response = await axios.post(
        `${API_URL}/profile/SignIn`,
        { primaryPhoneCountryCode, mobileNo: mobile },
        { headers: { env: "local" } }
      );

      const responseOtp = response?.data?.otp || "";
      const responseUserName = response?.data?.username || response?.data?.userName || "";
      const token = response?.data?.token;

      if (token) {
        sessionStorage.setItem("vbPendingToken", token);
      }

      setServerOtp(responseOtp ? String(responseOtp) : "");
      setUsername(responseUserName);
      setOtp("");
      setOtpSent(true);
      setSecondsLeft(90);

      if (!isResend) {
        toast.showSuccess("Sign-in response received. OTP section is now open.");
      } else {
        toast.showSuccess("OTP request refreshed.");
      }
    } catch (error) {
      console.error("OTP send failed", error);
      toast.showError("OTP request failed.");
    } finally {
      setLoading(false);
    }
  };

  const verifyOtp = async () => {
    if (!otp.trim()) {
      toast.showInfo("Please enter OTP.");
      return;
    }

    if (requiresConsent && (!acceptTerms || !acceptPrivacy)) {
      toast.showInfo("Please accept Privacy Policy and Terms & Conditions to continue.");
      return;
    }

    const token = sessionStorage.getItem("vbPendingToken");
    if (!token) {
      toast.showInfo("Session expired. Please request OTP again.");
      return;
    }

    setLoading(true);


    try {
      const digits = phoneNumber.replace(/\D/g, "");
      if (digits.length < 8) {
        toast.showInfo("Please enter a valid phone number.");
        return;
      }
      const primaryPhoneCountryCode = selectedCountry.dialCode;
     const validationResponse =  await axios.post(
        `${API_URL}/profile/validatetoken`,
        {
          otp: otp.trim(),
          primaryPhoneCountryCode,
          mobileNo: digits
        },
        { headers: { env: "local", token } }
      );

      toast.showSuccess("OTP verified successfully.");

      console.log("Validation response:", validationResponse?.data);

      const userId = validationResponse?.data?.value;
      sessionStorage.setItem("userId", userId);

      const profileResponse = await axios.get(`${API_URL}/profile/getProfileBasedOnUserId/${userId}`,
        { headers: { env: "local", token } }
      );

      const user = profileResponse?.data || {};
      sessionStorage.setItem("token", token);
      sessionStorage.removeItem("vbPendingToken");
      sessionStorage.setItem("vbUser", JSON.stringify(user));
      sessionStorage.setItem("vbPhone", buildMobileNumber());
      sessionStorage.setItem("consumerId", user?.consumerId || "");
      sessionStorage.setItem("mid", user?.primaryPhoneNumber || buildMobileNumber());
      sessionStorage.setItem("consumerorprovideId", user?.providerId || "");
      sessionStorage.setItem("vbIsProvider", user?.providerId ? "true" : "false");
      sessionStorage.setItem("vbActiveRole", "consumer");

      try {
        const defaultAddressResponse = await axios.get(
          `${API_URL}/profile/defaultAddress/${encodeURIComponent(userId)}`,
          { headers: { env: "local", token } }
        );

        const defaultAddressPayload = defaultAddressResponse?.data;
        const defaultAddress = Array.isArray(defaultAddressPayload)
          ? defaultAddressPayload[0] || null
          : Array.isArray(defaultAddressPayload?.responseData)
            ? defaultAddressPayload.responseData[0] || null
            : Array.isArray(defaultAddressPayload?.data)
              ? defaultAddressPayload.data[0] || null
              : defaultAddressPayload || null;

        if (defaultAddress) {
          sessionStorage.setItem("vbDefaultAddress", JSON.stringify(defaultAddress));
        } else {
          sessionStorage.removeItem("vbDefaultAddress");
        }
      } catch (defaultAddressError) {
        console.error("Default address fetch failed", defaultAddressError);
        sessionStorage.removeItem("vbDefaultAddress");
      }

      const nextPath = searchParams.get("next");
      const safeNextPath = nextPath && nextPath.startsWith("/") ? nextPath : "/consumer-dashboard";
      navigate(safeNextPath);
    } catch (error) {
      console.error("OTP verify failed", error);
      toast.showError("OTP verification failed.");
    } finally {
      setLoading(false);
    }
  };

  if (isLoggedIn) {
    const activeRole = sessionStorage.getItem("vbActiveRole") || "consumer";
    return <Navigate to={activeRole === "provider" ? "/provider-dashboard" : "/consumer-dashboard"} replace />;
  }

  return (
    <div className="ab-login-page">
      <section className="ab-login-shell">
        <div className="ab-login-orb ab-login-orb-one" aria-hidden="true" />
        <div className="ab-login-orb ab-login-orb-two" aria-hidden="true" />

        <section className="ab-login-authcard">
          <div className="ab-login-topbar">
            <div>
              <VyntezBuddyLogo compact />
            </div>

            <select className="ab-language-select" value={language} onChange={(event) => setLanguage(event.target.value)}>
              {options.map((item) => (
                <option key={item.value} value={item.value}>
                  {item.label}
                </option>
              ))}
            </select>
          </div>

          {username && <div className="ab-status-chip">{userLabel}</div>}

          <div className="ab-form-grid">
            <label>
              <span>{t("loginCountry")}</span>
              <select
                value={selectedCountryCode}
                onChange={(event) => setSelectedCountryCode(event.target.value)}
                disabled={otpSent}
              >
                {countries.map((country) => (
                  <option key={country.code} value={country.code}>
                    {country.flag} {country.dialCode}
                  </option>
                ))}
              </select>
              <small>Country code is auto-detected and can be changed.</small>
            </label>

            <label>
              <span>{t("loginPhone")}</span>
              <input
                type="tel"
                placeholder={t("loginPhonePlaceholder")}
                value={phoneNumber}
                onChange={(event) => setPhoneNumber(event.target.value.replace(/[^0-9]/g, ""))}
                disabled={otpSent}
              />
              <small>Use an active number to receive OTP and profile access.</small>
            </label>
          </div>

          {!otpSent && (
            <button type="button" className="vb-btn vb-btn-primary ab-login-action" onClick={() => sendOtp(false)} disabled={loading}>
              {loading ? t("loginSendingOtp") : t("loginSendOtp")}
            </button>
          )}

          {otpSent && (
            <div className="ab-otp-block">
              <div className="ab-otp-banner">
                <div>
                  <strong>OTP Verification</strong>
                  <p>Use the latest OTP generated for this phone number.</p>
                </div>
                <span>{serverOtp || "waiting for API value"}</span>
              </div>

              <label>
                <span>{t("loginOtp")}</span>
                <input
                  type="text"
                  inputMode="numeric"
                  maxLength={6}
                  value={otp}
                  onChange={(event) => setOtp(event.target.value.replace(/[^0-9]/g, ""))}
                  placeholder={t("loginOtpPlaceholder")}
                />
                <small>Enter the latest OTP generated for this mobile number.</small>
              </label>

              <div className="ab-resend-row">
                <button type="button" className="ab-link-btn" disabled={secondsLeft > 0 || loading} onClick={() => sendOtp(true)}>
                  {t("loginResendOtp")}
                </button>
                <span>{secondsLeft > 0 ? t("loginResendIn", { timer: formatTimer(secondsLeft) }) : t("loginResendReady")}</span>
              </div>

              {requiresConsent && (
                <div className="ab-consent-box">
                  <div className="ab-consent-head">
                    <h3>{username}</h3>
                    <p>New user consent is required</p>
                  </div>

                  <label>
                    <input
                      type="checkbox"
                      checked={acceptPrivacy}
                      onChange={(event) => setAcceptPrivacy(event.target.checked)}
                    />
                    <span>
                      {t("loginAgreePrefix")} <Link to="/privacy">{t("privacy")}</Link>.
                    </span>
                  </label>

                  <label>
                    <input
                      type="checkbox"
                      checked={acceptTerms}
                      onChange={(event) => setAcceptTerms(event.target.checked)}
                    />
                    <span>
                      {t("loginAgreePrefix")} <Link to="/terms">{t("terms")}</Link>.
                    </span>
                  </label>
                </div>
              )}

              <button type="button" className="vb-btn vb-btn-primary ab-login-action" onClick={verifyOtp} disabled={loading}>
                {loading ? t("loginVerifying") : t("loginSubmit")}
              </button>
            </div>
          )}
        </section>
      </section>
    </div>
  );
};

export default Login;
